/* eslint-disable react-hooks/exhaustive-deps */
import React, {useEffect} from "react"
import {useTranslation} from "react-i18next"
import {KlassappTableHeader, KlassappTable, BaseNewFilter} from "uiKit"
import {Icon} from "components/Icon"
import {KlassappTableHOC} from "HOC"
import {instructionalService} from "services"
import styles from "./MyCourses.module.css"
import {useModel} from "hooks"
import {AcademicPlans} from "types/academicPlans"

import {formatDateOnly, handleError, roundTwoDigits} from "helpers"
import {useQuery} from "@tanstack/react-query"

function MyCourses(props) {
    const {
        dispatch,
        dispatchFunc,
        page,
        total,
        pageSize,
        columns,
        data,
        allFields,
        fields,
        isLoadedTableFuncs,
        onClickCourseId,
        studentId,
        orderField,
        termIds
    } = props
    const [searchValue, setSearchValue] = React.useState("")
    const {t} = useTranslation(["course"])
    const model = useModel()

    const getFields = () => {
        return [
            t("course.code"),
            t("course.name"),
            "Schedule Name",
            "Sisid",
            "Grade (GPA)",
            "Status",
            t("course.lastLesson"),
            t("course.attendance"),
            "Instructors"
        ]
    }

    const getPageTitle = () => {
        return "Course"
    }

    const getColumns = () => {
        return [
            {
                title: t("course.code"),
                field: "courseCode",
                sortable: true,
                orderField: "courseCode"
            },
            {
                title: t("course.name"),
                field: "courseName",
                sortable: true,
                orderField: "courseName",
                style: {width: "250px"}
            },
            {
                title: "Schedule Name",
                field: "scheduleName",
                sortable: true,
                orderField: "scheduleName",
                style: {width: "250px"}
            },
            {
                title: "Sisid",
                field: "sisId",
                sortable: true,
                orderField: "courseSisId"
            },
            {
                title: "Grade (GPA)",
                field: "gpa",
                sortable: true,
                orderField: "courseGpa"
            },
            {
                title: "Status",
                field: "status",
                sortable: true,
                orderField: "courseStatus"
            },
            {
                title: t("course.lastLesson"),
                field: "lastLesson",
                sortable: true,
                orderField: "lastLessonDate"
            },
            {
                title: "Instructors",
                field: "instructors",
                sortable: true,
                orderField: "instructors"
            },
            {
                title: t("course.attendance"),
                field: "attendancePercentage",
                sortable: true,
                orderField: "courseAttendancePercentage"
            },
            {
                title: t("course.action"),
                field: "actionHtml"
            }
        ]
    }

    const checkStatus = (data: {
        status: AcademicPlans.AcademicPlanCourseStatus
        reviewStatus: AcademicPlans.AcademicPlanCourseReviewStatus
    }) => {
        if (data.reviewStatus === AcademicPlans.AcademicPlanCourseReviewStatus.Dropped) {
            return "Withdrawn"
        } else if (data.status === AcademicPlans.AcademicPlanCourseStatus.Suspended) {
            return "Suspended"
        } else if (
            data.status === AcademicPlans.AcademicPlanCourseStatus.Finished ||
            data.status === AcademicPlans.AcademicPlanCourseStatus.Completed
        ) {
            return "Completed"
        } else if (data.status === AcademicPlans.AcademicPlanCourseStatus.Failed) {
            return "Failed"
        } else if (data.status === AcademicPlans.AcademicPlanCourseStatus.Inprogress) {
            return "In-progress"
        } else if (data.status === AcademicPlans.AcademicPlanCourseStatus.Incomplete) {
            return "Incomplete"
        } else if (data.status === AcademicPlans.AcademicPlanCourseStatus.LoaIncomplete) {
            return "Loa-Incomplete"
        } else if (data.status === AcademicPlans.AcademicPlanCourseStatus.Loa) {
            return "Loa"
        } else {
            return ""
        }
    }

    const listCoursesQuery = useQuery<{
        data: any
        total: number
    }>(
        ["student-academic_courses", page, pageSize, searchValue, orderField?.field, orderField?.order, termIds],
        async () => {
            const params: any = {
                range: {page, pageSize},
                filter: {}
            }
            params.filter.termIds = termIds
            if (searchValue && searchValue !== "") {
                params.filter.search = searchValue
            }
            const profileId = model.profileId
            if (studentId) {
                params.filter.studentProfileIds = [studentId]
            } else if (!model.isStaffOrAdmin() && profileId) {
                params.filter.studentProfileIds = [profileId]
            }

            params.sort = orderField?.field
                ? {
                      orderBy: orderField.field,
                      orderDir: orderField.order
                  }
                : undefined

            const {data, total} = await instructionalService.getTermAndCourseList(params)
            const myCourses = data.map((item: any, index: number) => {
                item.id = index + 1
                item.termName = item.name
                item.status = checkStatus(item)
                item.lastLesson = formatDateOnly(item?.attendance?.date, model.getUserDateFormat())
                item.attendancePercent = item.attendance ? `${roundTwoDigits(item.attendance?.percentage)}%` : ""
                item.actionHtml = renderActionHtml(item)
                return item
            })

            return {
                data: myCourses,
                total
            }
        },
        {
            enabled: isLoadedTableFuncs,
            onError: (error) => handleError(error),
            onSuccess: (res) => {
                dispatch({
                    data: res.data,
                    total: res.total
                })
            }
        }
    )

    const renderActionHtml = (course) => {
        if (course.attendance?.actionNeededIds?.length > 0) {
            return <Icon icon="ALERT" />
        }
        return null
    }

    const onClickRowItem = (row) => {
        onClickCourseId(row.courseId, row.registeredScheduleId)
    }

    useEffect(() => {
        dispatch({isClassComponent: false})
        dispatchFunc([
            {key: "getPageTitle", func: getPageTitle},
            {key: "getListData", func: listCoursesQuery.refetch},
            {key: "getFields", func: getFields},
            {key: "getColumns", func: getColumns},
            {key: "onClickRowItem", func: onClickRowItem}
        ])
    }, [])

    const onApplyFilter = () => {
        //
    }

    const onClickClearFilter = () => {
        //
    }

    const onSearchInput = (newValue) => {
        setSearchValue(newValue)
    }

    return (
        <>
            <p className={styles.title}>Courses</p>
            <BaseNewFilter
                searchValue={searchValue}
                className={styles.filterWrap}
                onClick={onApplyFilter}
                onClickClear={onClickClearFilter}
                onSearchInput={onSearchInput}
            />
            <KlassappTableHeader
                isShowAction={false}
                actions={[]}
                page={page}
                total={total}
                defaultPageSize={pageSize}
                onChangePage={props.onChangePage}
                onChangeRowPerPage={props.onChangeRowPerPage}
                fields={fields}
                allFields={allFields}
                onChangeFields={props.onChangeFields}
                onChangeAllFields={props.onChangeAllFields}
                onDraggableColumn={props.onDraggableColumn}
            />
            <KlassappTable
                columns={columns}
                fields={fields}
                data={data}
                orderField={orderField}
                menuActions={[]}
                isLoading={listCoursesQuery.isFetching}
                allFields={allFields}
                isShowCheckedColumn={false}
                onClickRowItem={onClickRowItem}
                onChangeFields={props.onChangeFields}
                onUpdateRowData={props.onUpdateRowData}
                onUpdateTableData={props.onUpdateTableData}
                onClickSortColumn={props.onClickSortColumn}
                onDraggableColumn={props.onDraggableColumn}
                onChangeAllFields={props.onChangeAllFields}
            />
        </>
    )
}

export default KlassappTableHOC(MyCourses)
